/** The single owner of assistant-ui's unstable / behavior-critical surface.
 *
 * Everything here depends on contracts the library doesn't guarantee:
 * `unstable_createMessageConverter`, `convertLangChainMessages`'s silent
 * `undefined` for unknown message types, and `ExportedMessageRepository`'s
 * import shape. Keeping them behind this module (with the contract test suite
 * in `aui-adapter.test.ts`) means a version bump that changes any of it fails
 * in CI instead of in an investigator's session. Versions are pinned exactly
 * in package.json for the same reason.
 */

import {
  unstable_createMessageConverter as createMessageConverter,
  ExportedMessageRepository,
  type ThreadMessageLike,
} from '@assistant-ui/react'
import { convertLangChainMessages, type LangChainMessage } from '@assistant-ui/react-langgraph'

export type { LangChainMessage }

type ConversionMetadata = Parameters<typeof convertLangChainMessages>[1] & {
  errorBoundaries?: ReadonlySet<string>
}

const messageConverter = createMessageConverter(
  (message: LangChainMessage, meta: ConversionMetadata) => {
    const converted = convertLangChainMessages(message, meta)
    // LangChain ToolMessage.name is optional and serializes as null. The
    // library treats only undefined as unnamed; let it pair by tool_call_id
    // and retain the name from the assistant's original tool call.
    if (!Array.isArray(converted) && converted?.role === 'tool' && !converted.toolName) {
      return { ...converted, toolName: undefined }
    }
    if (
      message.id &&
      meta.errorBoundaries?.has(message.id) &&
      !Array.isArray(converted) &&
      converted?.role === 'assistant'
    ) {
      return { ...converted, convertConfig: { joinStrategy: 'none' as const } }
    }
    return converted
  }
)

/** Error rows must not donate their status/retry metadata to the next attempt.
 * joinStrategy=none splits AFTER a message, so mark both error boundaries. */
function errorBoundaries(messages: LangChainMessage[]): Set<string> {
  const ids = new Set<string>()
  let previousAssistant: string | undefined
  for (const message of messages) {
    if (message.type === 'human' || message.type === 'system') previousAssistant = undefined
    if (message.type !== 'ai') continue
    if (message.status?.type === 'incomplete') {
      if (previousAssistant) ids.add(previousAssistant)
      if (message.id) ids.add(message.id)
    }
    previousAssistant = message.id
  }
  return ids
}

// convertLangChainMessages silently returns undefined for any message type it
// doesn't know (only human/ai/tool/system are handled) — and an undefined
// entry crashes assistant-ui's grouping with "can't access property 'role'".
// Filter to renderable types before converting so an unexpected backend
// message degrades to a console warning instead of unmounting the thread.
const RENDERABLE_TYPES = new Set(['human', 'ai', 'tool', 'system'])

// Warn once per unknown type — the converter re-runs on every stream chunk,
// and a persistent unrenderable message would otherwise spam the console.
const warnedUnrenderableTypes = new Set<string | undefined>()

/** Propagate the HTTP run state into message conversion too: assistant-ui derives
 * thread.isRunning from the last assistant MESSAGE's status, not just the
 * external-store flag. Omitting this marks the thread idle between tool calls. */
export function toThreadMessagesSafe(messages: LangChainMessage[], isRunning = false) {
  const renderable = messages.filter(m => {
    const type = (m as { type?: string }).type
    if (type && RENDERABLE_TYPES.has(type)) return true
    if (!warnedUnrenderableTypes.has(type)) {
      warnedUnrenderableTypes.add(type)
      console.warn('[agent] dropping message with unrenderable type:', type)
    }
    return false
  })
  // A retry reuses the original human id, so its old terminal error can still
  // be the tail while HTTP is open. Only the transport ending makes this turn
  // terminal. Clear explicit statuses in the active tail, not in older turns.
  const lastUser = renderable.reduce(
    (last, message, index) => (message.type === 'human' ? index : last),
    -1
  )
  const active = isRunning
    ? renderable.map((message, index) =>
        index > lastUser && message.type === 'ai' && message.status
          ? { ...message, status: undefined }
          : message
      )
    : renderable
  // The converter otherwise drops a trailing error's status when joining it
  // to preceding tool output. Metadata preserves the terminal error on close.
  const last = active.at(-1)
  const error =
    last?.type === 'ai' && last.status?.type === 'incomplete' && last.status.reason === 'error'
      ? last.status.error
      : undefined
  const metadata: ConversionMetadata = { error, errorBoundaries: errorBoundaries(renderable) }
  return messageConverter.toThreadMessages(active, isRunning, metadata)
}

/** The payload for `runtime.thread.import` when (re)building the thread from
 * paginated history. */
export function buildHistoryRepository(messages: LangChainMessage[]) {
  return ExportedMessageRepository.fromArray(
    toThreadMessagesSafe(messages) as unknown as ThreadMessageLike[]
  )
}
